import { useEffect, useState } from "react";
import { Dropzone } from "./components/Dropzone";
import { CandidatesPage } from "./components/CandidatesPage";
import { MatrixManager } from "./components/MatrixManager";
import { PositionManager } from "./components/PositionManager";
import { api } from "./lib/api";
import { extractTextFromFile } from "./lib/extractText";
import type { Candidate, HiringMatrix, Position } from "./types";

type Page = "screen" | "positions" | "matrices" | "candidates" | "archived";

interface ScreeningJob {
  id: string;
  filename: string;
  status: "extracting" | "ocr" | "screening" | "done" | "error";
  statusDetail?: string;
  error?: string;
  candidate?: Candidate;
}

export default function App() {
  const [page, setPage] = useState<Page>("screen");
  const [positions, setPositions] = useState<Position[]>([]);
  const [matrices, setMatrices] = useState<HiringMatrix[]>([]);
  const [selectedPositionId, setSelectedPositionId] = useState<number | null>(null);
  const [selectedMatrixId, setSelectedMatrixId] = useState<number | null>(null);
  const [jobs, setJobs] = useState<ScreeningJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [newCandidateCount, setNewCandidateCount] = useState(0);

  const refreshPositions = async () => {
    const { positions } = await api.listPositions();
    setPositions(positions);
    return positions;
  };

  const refreshMatrices = async (posId?: number) => {
    const { matrices } = await api.listMatrices(posId);
    setMatrices(matrices);
    return matrices;
  };

  // Initial load
  useEffect(() => {
    (async () => {
      const pos = await refreshPositions();
      if (pos.length > 0) {
        setSelectedPositionId(pos[0].id);
        const mats = await refreshMatrices(pos[0].id);
        if (mats.length > 0) {
          setSelectedMatrixId(mats[0].id);
        }
      }
      setLoading(false);
    })();
  }, []);

  // When position changes, reload matrices for it
  useEffect(() => {
    if (selectedPositionId == null) {
      setMatrices([]);
      setSelectedMatrixId(null);
      return;
    }
    (async () => {
      const mats = await refreshMatrices(selectedPositionId);
      if (mats.length > 0) {
        setSelectedMatrixId(mats[0].id);
      } else {
        setSelectedMatrixId(null);
      }
    })();
  }, [selectedPositionId]);

  const selectedMatrix = matrices.find((m) => m.id === selectedMatrixId) ?? null;

  const handleFiles = async (files: File[]) => {
    if (!selectedPositionId || !selectedMatrixId) {
      alert("Select a position and hiring matrix first.");
      return;
    }
    let hasSuccess = false;
    for (const file of files) {
      const jobId = crypto.randomUUID();
      const job: ScreeningJob = {
        id: jobId,
        filename: file.name,
        status: "extracting",
      };
      setJobs((js) => [job, ...js]);

      try {
        const result = await extractTextFromFile(file, (detail) => {
          const isOcr = detail.toLowerCase().includes("ocr") || detail.toLowerCase().includes("image");
          setJobs((js) =>
            js.map((j) =>
              j.id === jobId
                ? { ...j, status: isOcr ? "ocr" : "extracting", statusDetail: detail }
                : j
            )
          );
        });
        setJobs((js) =>
          js.map((j) => (j.id === jobId ? { ...j, status: "screening", statusDetail: undefined } : j))
        );
        const { candidate } = await api.screen({
          position_id: selectedPositionId,
          matrix_id: selectedMatrixId,
          filename: file.name,
          resume_text: result.text,
          photo: result.photo,
        });
        setJobs((js) =>
          js.map((j) =>
            j.id === jobId ? { ...j, status: "done", candidate } : j
          )
        );
        hasSuccess = true;
        setNewCandidateCount((c) => c + 1);

        // Auto-remove done job after 3 seconds
        setTimeout(() => {
          setJobs((js) => js.filter((j) => j.id !== jobId));
        }, 3000);
      } catch (e) {
        setJobs((js) =>
          js.map((j) =>
            j.id === jobId
              ? { ...j, status: "error", error: (e as Error).message }
              : j
          )
        );
      }
    }
    // Redirect to Candidates page after screening completes
    if (hasSuccess) {
      setPage("candidates");
    }
  };

  return (
    <div className="flex min-h-full flex-col">
      {/* Header */}
      <header className="sticky top-0 z-10 border-b border-surface-secondary bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div>
            <h1 className="text-[28px] font-bold leading-[1.43] text-near-black">
              Resume Screener
            </h1>
            <p className="text-sm font-medium text-text-secondary">
              Powered by <span className="font-bold text-emerald-600">DitoSoft</span> AI
            </p>
          </div>
          {/* Nav tabs */}
          <nav className="flex gap-1">
            <NavTab
              active={page === "screen"}
              onClick={() => setPage("screen")}
            >
              Screen
            </NavTab>
            <NavTab
              active={page === "positions"}
              onClick={() => setPage("positions")}
            >
              Positions
            </NavTab>
            <NavTab
              active={page === "matrices"}
              onClick={() => setPage("matrices")}
            >
              Matrices
            </NavTab>
            <NavTab
              active={page === "candidates"}
              onClick={() => {
                setPage("candidates");
                setNewCandidateCount(0);
              }}
              badge={newCandidateCount > 0 && page !== "candidates" ? newCandidateCount : undefined}
            >
              Candidates
            </NavTab>
            <NavTab
              active={page === "archived"}
              onClick={() => setPage("archived")}
            >
              Archived
            </NavTab>
          </nav>
        </div>
      </header>

      <main className="mx-auto flex w-full max-w-6xl flex-1 flex-col px-6 py-8">
        {loading ? (
          <p className="text-text-secondary">Loading…</p>
        ) : page === "screen" ? (
          <ScreenPage
            positions={positions}
            matrices={matrices}
            selectedPositionId={selectedPositionId}
            selectedMatrixId={selectedMatrixId}
            selectedMatrix={selectedMatrix}
            jobs={jobs}
            onPositionChange={setSelectedPositionId}
            onMatrixChange={setSelectedMatrixId}
            onFiles={handleFiles}
            onClearJobs={() => setJobs([])}
            noMatrices={matrices.length === 0}
            onGoToMatrices={() => setPage("matrices")}
          />
        ) : page === "positions" ? (
          <PositionManager
            positions={positions}
            onChange={async () => {
              const pos = await refreshPositions();
              if (
                selectedPositionId &&
                !pos.find((p) => p.id === selectedPositionId)
              ) {
                setSelectedPositionId(pos[0]?.id ?? null);
              }
            }}
          />
        ) : page === "matrices" ? (
          <MatrixManager
            positions={positions}
            onPositionsChange={async () => {
              const pos = await refreshPositions();
              if (
                selectedPositionId &&
                !pos.find((p) => p.id === selectedPositionId)
              ) {
                setSelectedPositionId(pos[0]?.id ?? null);
              }
            }}
          />
        ) : page === "candidates" ? (
          <CandidatesPage positions={positions} />
        ) : (
          <CandidatesPage positions={positions} archivedMode />
        )}
      </main>
    </div>
  );
}

function NavTab({
  active,
  onClick,
  badge,
  children,
}: {
  active: boolean;
  onClick: () => void;
  badge?: number;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={[
        "relative rounded-btn px-4 py-2 text-sm font-medium transition-colors",
        active
          ? "bg-near-black text-white"
          : "bg-surface-secondary text-text-focused hover:bg-near-black hover:text-white",
      ].join(" ")}
    >
      {children}
      {badge != null && (
        <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-rausch px-1 text-[10px] font-bold tabular-nums text-white">
          {badge}
        </span>
      )}
    </button>
  );
}

function ScreenPage({
  positions,
  matrices,
  selectedPositionId,
  selectedMatrixId,
  selectedMatrix,
  jobs,
  onPositionChange,
  onMatrixChange,
  onFiles,
  onClearJobs,
  noMatrices,
  onGoToMatrices,
}: {
  positions: Position[];
  matrices: HiringMatrix[];
  selectedPositionId: number | null;
  selectedMatrixId: number | null;
  selectedMatrix: HiringMatrix | null;
  jobs: ScreeningJob[];
  onPositionChange: (id: number | null) => void;
  onMatrixChange: (id: number | null) => void;
  onFiles: (files: File[]) => void;
  onClearJobs: () => void;
  noMatrices: boolean;
  onGoToMatrices: () => void;
}) {
  const canScreen = selectedPositionId != null && selectedMatrixId != null;

  return (
    <div className="flex flex-1 flex-col gap-6 pb-2">
      {/* TOP: Dropzone — the primary action, fills available space */}
      <div className="flex flex-1 flex-col rounded-card bg-white p-6 shadow-card">
        {/* Position + matrix selectors inline */}
        <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.32px] text-text-secondary">
              Position
            </label>
            <select
              className="mt-1 w-full rounded-btn border border-border-card bg-white px-3 py-2 text-sm text-near-black focus:outline-none focus:shadow-focus"
              value={selectedPositionId ?? ""}
              onChange={(e) => onPositionChange(Number(e.target.value) || null)}
            >
              {positions.length === 0 && (
                <option value="">— no positions —</option>
              )}
              {positions.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.title}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.32px] text-text-secondary">
              Hiring matrix
            </label>
            <select
              className="mt-1 w-full rounded-btn border border-border-card bg-white px-3 py-2 text-sm text-near-black focus:outline-none focus:shadow-focus"
              value={selectedMatrixId ?? ""}
              onChange={(e) => onMatrixChange(Number(e.target.value) || null)}
            >
              {matrices.length === 0 && (
                <option value="">— no matrices —</option>
              )}
              {matrices.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Matrix preview */}
        {selectedMatrix && (
          <div className="mb-4 rounded-badge bg-surface-secondary p-3 text-xs text-text-secondary">
            <div className="font-semibold uppercase tracking-[0.32px] text-text-focused">
              {selectedMatrix.name}
            </div>
            <ul className="mt-1.5 space-y-0.5">
              {selectedMatrix.criteria.map((c) => (
                <li key={c.name}>
                  <span className="font-medium text-near-black">{c.name}</span>{" "}
                  <span className="text-text-secondary">(w {c.weight})</span> —{" "}
                  {c.requirement}
                </li>
              ))}
            </ul>
          </div>
        )}

        {noMatrices && (
          <div className="mb-4 rounded-badge bg-rausch-light p-3 text-sm text-rausch">
            No hiring matrices found.{" "}
            <button
              onClick={onGoToMatrices}
              className="font-semibold underline"
            >
              Create one
            </button>{" "}
            to start screening.
          </div>
        )}

        <Dropzone disabled={!canScreen} className="flex-1" onFiles={onFiles} />
      </div>

      {/* Upload status */}
      {jobs.length > 0 && (
        <div className="rounded-card bg-white p-5 shadow-card">
          <div className="flex items-center justify-between">
            <div className="text-[8px] font-bold uppercase tracking-[0.32px] text-text-secondary">
              Uploads
            </div>
            <button
              onClick={onClearJobs}
              className="text-xs font-medium text-text-secondary transition-colors hover:text-error-text"
            >
              Clear
            </button>
          </div>
          <ul className="mt-2 space-y-1.5 text-sm">
            {jobs.map((j) => (
              <li key={j.id} className="flex items-center justify-between">
                <span className="truncate font-medium text-near-black">
                  {j.filename}
                </span>
                <StatusBadge job={j} />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ job }: { job: ScreeningJob }) {
  const map: Record<ScreeningJob["status"], string> = {
    extracting: "bg-surface-secondary text-text-focused",
    ocr: "bg-amber-100 text-amber-800",
    screening: "bg-rausch-light text-rausch",
    done: "bg-emerald-100 text-emerald-800",
    error: "bg-rose-100 text-error-text",
  };
  const label: Record<ScreeningJob["status"], string> = {
    extracting: job.statusDetail || "Extracting text…",
    ocr: job.statusDetail || "Running OCR…",
    screening: "Screening with LLM…",
    done: "Done",
    error: `Error: ${job.error ?? ""}`,
  };
  const isProcessing = job.status !== "done" && job.status !== "error";

  return (
    <span
      className={`ml-2 inline-flex items-center gap-1.5 max-w-[60%] truncate rounded-badge px-2.5 py-0.5 text-xs font-medium ${map[job.status]}`}
      title={label[job.status]}
    >
      {isProcessing && (
        <svg className="h-3 w-3 animate-spin" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      )}
      {label[job.status]}
    </span>
  );
}
